import React, { Component, createContext } from 'react'
import { withRouter } from 'next/router'
import withUiContext from '../consumerHOC/UIConsumer'

import { api, config, asyncFunctionDecorator, setCookie, removeCookie, getUser } from "helpers"

const UserContext = createContext({})
export const UserConsumer = UserContext.Consumer

class UserProvider extends Component {
    state = {
        ...this.props.userProps
    }

    methods = {
        readNotify: this.readNotify.bind(this),

        //! Debate
        createDebate: this.createDebate.bind(this),
        updateDebate: this.updateDebate.bind(this),
        inviteDebate: this.inviteDebate.bind(this),

        //! Feedback
        checkLike: this.checkLike.bind(this),
        addFeedback: this.addFeedback.bind(this),
        toggleFeedbackLike: this.toggleFeedbackLike.bind(this),

        //! Comment
        addComment: this.addComment.bind(this),
        toggleCommentLike: this.toggleCommentLike.bind(this),
        checkCommentLiked: this.checkCommentLiked.bind(this),

        toggleFollow: this.toggleFollow.bind(this),
        toggleFavorite: this.toggleFavorite.bind(this),
        editProfile: this.editProfile.bind(this),
        changeEmail: this.changeEmail.bind(this),
        changePassword: this.changePassword.bind(this),
        changeSettings: this.changeSettings.bind(this),
        login: this.login.bind(this),
        socialLogin: this.socialLogin.bind(this),
        setAuth: this.setAuth.bind(this),
        checkFirstStep: this.checkFirstStep.bind(this),
        register: this.register.bind(this),
        logout: this.logout.bind(this),
        forgotPassword: this.forgotPassword.bind(this),
        resetPassword: this.resetPassword.bind(this),
        deactivateAccount: this.deactivateAccount.bind(this)
    }

    componentDidUpdate(prevProps) {
        if (prevProps.router.asPath !== this.props.router.asPath) {
            getUser(this.props.router.locale, this.state, this.props.router).then((res) => {
                this.setState({ ...res })
            })
        }
    }

    readNotify() {
        return api.post(`${config.routePrefix.users}/read-notify`)
    }

    createDebate(data) {
        return asyncFunctionDecorator(() =>
            api.post(`${config.routePrefix.debates}`, data)
        )
    }

    updateDebate(id, data) {
        return asyncFunctionDecorator(() =>
            api.put(`${config.routePrefix.debates}/${id}`, data)
        )
    }

    inviteDebate(data) {
        return asyncFunctionDecorator(() =>
            api.post(`${config.routePrefix.debates}/invite`, data)
        )
    }

    addFeedback(data) {
        return asyncFunctionDecorator(() =>
            api.post(`${config.routePrefix.feedbacks}`, data)
        )
    }

    toggleFeedbackLike(data) {
        return asyncFunctionDecorator(() =>
            api.post(`${config.routePrefix.feedbacks}/toggle-like`, data)
        )
    }

    checkLike(id) {
        if (this.state.isLoggedIn) {
            const like = this.state.currentUser.likes?.find(like => like.feedback_id == id)

            if (like) {
                return like.type
            }
        }
        return 'default';
    }

    addComment(data) {
        return asyncFunctionDecorator(() =>
            api.post(`${config.routePrefix.comments}/create-comment`, data)
        )
    }

    toggleCommentLike(data) {
        return asyncFunctionDecorator(() =>
            api.post(`${config.routePrefix.comments}/toggle-like`, data)
        )
    }

    checkCommentLiked(id) {
        if (this.state.isLoggedIn) {
            const like = this.state.currentUser.commentLikes?.find(like => like.comment_id == id)
            if (like) {
                return true;
            }
        }
        return false;
    }

    toggleFollow(followingId) {
        return asyncFunctionDecorator(() =>
            api.post(`${config.routePrefix.users}/follow`, { followingId: followingId })
        )
    }

    toggleFavorite(debateId) {
        return asyncFunctionDecorator(() =>
            api.post(`${config.routePrefix.debates}/favorite`, { debateId: debateId })
        )
    }

    editProfile(data) {
        return asyncFunctionDecorator(() =>
            api.patch(`${config.routePrefix.users}/edit-profile`, data).then(() => {
                this.handleRouterRedirect(`/${this.state.currentUser.username}`)
            })
        )
    }

    changeEmail(data) {
        return asyncFunctionDecorator(() =>
            api.patch(`${config.routePrefix.users}/change-email`, data)
        )
    }

    changePassword(data) {
        return asyncFunctionDecorator(() =>
            api.patch(`${config.routePrefix.users}/change-password`, data)
        )
    }

    changeSettings(data) {
        return asyncFunctionDecorator(() =>
            api.patch(`${config.routePrefix.users}/change-settings`, data)
        )
    }

    login(data) {
        return asyncFunctionDecorator(() =>
            api.post(`${config.routePrefix.auth}/login`, data).then(({ accessToken, user }) => {
                setCookie('accessToken', accessToken)
                setCookie('username', user.username)

                this.setState({
                    isLoggedIn: true,
                    user: user,
                    currentUser: user
                }, () => {
                    this.props.closePopup()
                })
            })
        )
    }

    socialLogin(provider) {
        return window.location.href = `${process.env.NEXT_PUBLIC_SERVER_API_PATH}${config.routePrefix.auth}/${provider}`
    }

    setAuth(token, username) {
        setCookie('accessToken', token)
        setCookie('username', username)
        this.setState({
            isLoggedIn: true
        }, () => {
            this.handleRouterRedirect()
        })
    }

    checkFirstStep(data) {
        return asyncFunctionDecorator(() =>
            api.post(`${config.routePrefix.auth}/check-first-step`, data)
        )
    }

    register(data) {
        return asyncFunctionDecorator(() =>
            api.post(`${config.routePrefix.auth}/register`, data).then(({ accessToken, user }) => {
                setCookie('accessToken', accessToken)
                setCookie('username', user.username)

                this.setState({
                    isLoggedIn: true,
                    user: user,
                    currentUser: user
                }, () => {
                    this.handleRouterRedirect()
                })
            })
        )
    }

    logout() {
        removeCookie('accessToken')
        removeCookie('username')
        this.setState({
            isLoggedIn: false,
            user: null,
            currentUser: null
        })
        this.handleRouterRedirect()
    }

    forgotPassword(data) {
        return asyncFunctionDecorator(() =>
            api.post(`${config.routePrefix.auth}/forgot-password`, data)
        )
    }

    resetPassword(data) {
        return asyncFunctionDecorator(() =>
            api.post(`${config.routePrefix.auth}/reset-password`, data)
        )
    }

    deactivateAccount() {
        return asyncFunctionDecorator(() =>
            api.post(`${config.routePrefix.users}/deactivate-account`).then(() => {
                removeCookie('accessToken')
                this.setState({
                    isLoggedIn: false,
                    user: null,
                    currentUser: null
                })
                this.handleRouterRedirect()
            })
        )
    }

    handleRouterRedirect(path) {
        this.props.router.push(path || '/')
    }

    render() {
        return (
            <UserContext.Provider value={{ ...this.state, ...this.methods }}>
                {this.props.children}
            </UserContext.Provider>
        )
    }
}

export default withRouter(withUiContext(UserProvider, ['closePopup']))

